import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, UserPlus, ChevronRight, X, AlertCircle } from 'lucide-react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useTreatment } from '../../context/TreatmentContext';
import TreatmentSubHeader from '../../components/TreatmentSubHeader';
import '../../styles/treatment.css';

const TreatmentPatients = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    gender: 'Male',
    contactNumber: '',
    email: '',
    address: '',
  });
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const { currentPatient, setCurrentPatient } = useTreatment();
  const navigate = useNavigate();

  const fetchPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getPatients();
      setPatients(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load patients');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!formData.name.trim()) {
      setSubmitError('Name is required');
      return;
    }
    const age = parseInt(formData.age, 10);
    if (!formData.age || isNaN(age) || age < 1 || age > 150) {
      setSubmitError('Please enter a valid age (1-150)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        age,
        gender: formData.gender || 'Male',
        contactNumber: formData.contactNumber.trim() || undefined,
        email: formData.email.trim() || undefined,
        address: formData.address.trim() || undefined,
      };
      if (isAuthenticated() && user?.id) {
        payload.userId = user.id;
      }
      const created = await apiService.createPatient(payload);
      setPatients((prev) => [...prev, created]);
      setCurrentPatient(created);
      setShowCreateForm(false);
      setFormData({ name: '', age: '', gender: 'Male', contactNumber: '', email: '', address: '' });
      navigate('/treatment/assess');
    } catch (err) {
      setSubmitError(err.message || 'Failed to create patient');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setCurrentPatient(patient);
    navigate('/treatment');
  };

  return (
    <div className="treatment-page">
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center">
            <h1>Patients</h1>
            <p>Manage patient profiles and select a patient for assessment and recommendations.</p>
          </div>
        </div>
      </section>
      <TreatmentSubHeader />

      <section className="section">
        <div className="container">
          <div className="treatment-breadcrumb">
            <Link to="/treatment">AYUSH Care</Link>
            <span> / Patients</span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 className="section-title" style={{ marginBottom: 0 }}>All patients</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCreateForm(true)}
            >
              <UserPlus size={18} />
              Add patient
            </button>
          </div>

          {showCreateForm && (
            <div className="treatment-card" style={{ marginBottom: '1.5rem' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3>Create patient profile</h3>
                  <button
                    type="button"
                    className="btn btn-outline btn-small"
                    onClick={() => {
                      setShowCreateForm(false);
                      setSubmitError('');
                    }}
                    aria-label="Close"
                  >
                    <X size={18} />
                  </button>
                </div>
                <form className="treatment-form" onSubmit={handleSubmit}>
                  {submitError && (
                    <div className="error-state" style={{ padding: '0.5rem 0', textAlign: 'left' }}>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <AlertCircle size={18} /> {submitError}
                      </p>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Name *</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      maxLength={200}
                      placeholder="Full name"
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label" htmlFor="age">Age *</label>
                      <input
                        id="age"
                        name="age"
                        type="number"
                        min={1}
                        max={150}
                        className="form-control"
                        value={formData.age}
                        onChange={handleInputChange}
                        required
                        placeholder="Age"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="gender">Gender</label>
                      <select
                        id="gender"
                        name="gender"
                        className="form-control"
                        value={formData.gender}
                        onChange={handleInputChange}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="contactNumber">Contact</label>
                    <input
                      id="contactNumber"
                      name="contactNumber"
                      type="text"
                      className="form-control"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      placeholder="Phone"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      className="form-control"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="address">Address</label>
                    <textarea
                      id="address"
                      name="address"
                      className="form-control"
                      rows={2}
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Address"
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? 'Creating...' : 'Create patient'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {loading && (
            <div className="loading-state">
              <div className="spinner" />
              <p>Loading patients...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-outline" onClick={fetchPatients}>Retry</button>
            </div>
          )}

          {!loading && !error && patients.length === 0 && !showCreateForm && (
            <div className="treatment-empty">
              <Users size={48} style={{ color: 'var(--text-muted)', marginBottom: '1rem' }} />
              <p>No patients yet. Create a patient profile to start.</p>
              <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                <UserPlus size={18} /> Add patient
              </button>
            </div>
          )}

          {!loading && !error && patients.length > 0 && (
            <div className="treatment-quicklinks" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
              {patients.map((p) => (
                <div
                  key={p.id}
                  className="card treatment-quicklink-card"
                  style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  onClick={() => handleSelectPatient(p)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3>{p.name}</h3>
                    <ChevronRight size={20} className="treatment-quicklink-arrow" />
                  </div>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {p.gender}, {p.age} years
                    {p.prakriti && ` · ${p.prakriti}`}
                  </p>
                  {currentPatient?.id === p.id && (
                    <span style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: 'var(--accent-green)' }}>
                      Current patient
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TreatmentPatients;
